import { useRef } from "react";
import {Card, Space, Button, Typography, Segmented, Dropdown} from "antd";
import {
    DownloadOutlined,
    FileExcelOutlined, FileImageOutlined, FileJpgOutlined,
    FilePdfOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import EngagementTable from "./engagement.table";
import { useEngagementTableMatrixData } from "../../../hooks/analytics";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { exportChartAsPDF } from "../../../utils/export.utils";
import { LOGOS } from "../../../utils/constant";

const { Text } = Typography;

export default function EngagementTablePanel({
                                                 clickData,
                                                 selectedDesignations = [],
                                                 selectedConditions = [],
                                                 dateRange, // [startDayjs, endDayjs]
                                                 period,
                                                 setPeriod,
                                                 groupBy,
                                                 setGroupBy,
                                                 onResetZoom, // no-op here; kept for signature symmetry
                                             }) {
    const containerRef = useRef(null);

    // matrix (latest -> oldest), synced with the same period & groupBy as charts
    const { columns, dataSource, meta } = useEngagementTableMatrixData({
        clickData: clickData.filter((it) => {
            // keep the same filtering semantics as charts
            const okDesig =
                !selectedDesignations?.length ||
                (it?.user?.designation && selectedDesignations.includes(it.user.designation));
            const okCond =
                !selectedConditions?.length ||
                (it?.conditionName && selectedConditions.includes(it.conditionName));
            return okDesig && okCond;
        }),
        period,
        groupBy,
        dateRange,
    });

    const filenameBase = `analytics_${groupBy}_table_${dayjs(dateRange?.[0]).format(
        "YYYYMMDD"
    )}_${dayjs(dateRange?.[1]).format("YYYYMMDD")}`;

    const exportCSV = () => {
        const header = [columns.map((c) => `"${String(c.title).replace(/"/g, '""')}"`).join(",")];
        const body = dataSource.map((row) =>
            columns
                .map((c) => (c.dataIndex === "entity" ? row.entity : row[c.dataIndex] ?? 0))
                .join(",")
        );
        const blob = new Blob([header.concat(body).join("\n")], { type: "text/csv;charset=utf-8" });
        saveAs(blob, `${filenameBase}.csv`);
    };

    const exportPDF = async () => {
        await exportChartAsPDF({
            chartElement: containerRef.current,
            filename: `${filenameBase}.pdf`,
            footnote: "OPC Analytics",
            logos: LOGOS,
        });
    };

    const exportMenuItems = [
        {
            key: "csv",
            icon: <FileExcelOutlined />,
            label: "Export as CSV",
            onClick: () => exportCSV(),
        },
        {
            key: "pdf",
            icon: <FilePdfOutlined />,
            label: "Export as PDF",
            onClick:  () => exportPDF()

        },
    ];

    return (
        <Card ref={containerRef}
              extra = {
                  <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12 }} >
                      <Space
                          style={{width: "100%", justifyContent: "space-between", marginBottom: 12}}
                          wrap
                      >
                          {/*<Space align="baseline" wrap>*/}
                          {/*    <Text strong>*/}
                          {/*        Table · By {groupBy === "designation" ? "Designation" : "Condition"}*/}
                          {/*    </Text>*/}
                          {/*    <Text type="secondary">Total events: {meta.total}</Text>*/}
                          {/*</Space>*/}

                          {/* Responsive, shared controls — same state as charts */}
                              <Segmented
                                  size="small"
                                  value={period}
                                  onChange={setPeriod}
                                  options={["Day", "Week", "Month", "Year"]}
                              />
                              <Segmented
                                  size="small"
                                  value={groupBy}
                                  onChange={setGroupBy}
                                  options={[
                                      {label: "By Condition", value: "condition"},
                                      {label: "By Designation", value: "designation"},
                                      { label: "By Hospital",    value: "hospital" },
                                      { label: "By District",    value: "district" },
                                      { label: "By Gender",      value: "gender" },
                                  ]}
                              />
                              <Dropdown
                                  menu={{items: exportMenuItems}}
                                  placement="bottomRight"
                                  arrow
                              >
                                  <Button icon={<DownloadOutlined/>}/>
                              </Dropdown>
                      </Space>
                  </div>

              }
        >


            <EngagementTable columns={columns} dataSource={dataSource} />
        </Card>
    );
}
